/**
 * RequiredDocsView.jsx — shared list renderer for vendor-required documents.
 *
 * Given a list of {dataroom_id, slot_id} pairs + a Set of filled slot keys
 * ("dataroom_id::slot_id"), shows each doc with a ✓ if uploaded or an empty
 * circle if missing.  Looks up the human-readable name from the vault
 * taxonomy passed in.
 *
 * Used by: marketplace ProductDrawer (under each vendor row) and VendorProfile.
 */
import React from 'react';
import { Check } from '@phosphor-icons/react';

const RequiredDocsView = ({ items = [], filledSlots = new Set(), taxonomy = [], accent = '#10b981', compact = false }) => {
  if (!items.length) return null;

  // Build a quick {dataroom_id: {name, slots: {slot_id: name}}} lookup.
  const lookup = {};
  taxonomy.forEach((dr) => {
    lookup[dr.id] = { name: dr.name, slots: {} };
    dr.slots.forEach((s) => { lookup[dr.id].slots[s.id] = s.name; });
  });

  const filledCount = items.filter((it) => filledSlots.has(`${it.dataroom_id}::${it.slot_id}`)).length;

  return (
    <div className="space-y-1.5" data-testid="required-docs-view">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-bold text-text-muted">
        <span>Documents required ({items.length})</span>
        <span style={{ color: filledCount === items.length ? accent : 'var(--text-muted)' }}>
          {filledCount}/{items.length} ready
        </span>
      </div>
      <div className={compact ? 'grid grid-cols-1 sm:grid-cols-2 gap-1' : 'space-y-1'}>
        {items.map((it, i) => {
          const filled = filledSlots.has(`${it.dataroom_id}::${it.slot_id}`);
          const drName = lookup[it.dataroom_id]?.name || it.dataroom_id;
          const slotName = lookup[it.dataroom_id]?.slots[it.slot_id] || it.slot_id;
          return (
            <div key={`${it.dataroom_id}-${it.slot_id}-${i}`} className="flex items-start gap-2 text-[11px]" data-testid={`required-doc-${it.slot_id}`}>
              {filled ? (
                <span className="w-4 h-4 rounded-full inline-flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${accent}1a`, color: accent }}>
                  <Check size={9} weight="bold" />
                </span>
              ) : (
                <span className="w-4 h-4 rounded-full inline-flex items-center justify-center flex-shrink-0 mt-0.5 border border-dashed border-border" />
              )}
              <div className="flex-1 min-w-0">
                <div className={`leading-tight truncate ${filled ? 'text-text-secondary' : 'text-text-primary font-medium'}`}>{slotName}</div>
                <div className="text-[10px] text-text-muted truncate">{drName}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RequiredDocsView;
